import Hls from 'hls.js';
import { useCallback, useMemo, useRef } from 'react';
import { hlsConfig } from '../config/videoPlayer.config';
import { getAvailableVideoTimeRange } from '../utils/videoPlayer.utils';
import { attachHlsDiagnostics, logHlsDiagnostic } from '../utils/videoPlayerDiagnostics.utils';

/**
 * Creates, attaches, and clears the video stream source.
 *
 * The hook owns HLS setup, native HLS fallback, HLS event listeners, stream diagnostics,
 * and stream teardown. It reports stream readiness and fatal stream errors back to the caller.
 */
const useVideoStreamSource = ({ isCurrentRequest, updateReadyVideoProgress }) => {
  const hlsRef = useRef(null);
  const hasAttachedStreamRef = useRef(false);

  // Gets the most recent value from the attached stream ref. Needs to be function to avoid stale closure issues
  const hasAttachedStream = useCallback(() => hasAttachedStreamRef.current, []);

  // Destroys the current stream and resets the video element to a clean state
  const clearStream = useCallback((video) => {
    const hls = hlsRef.current;
    hlsRef.current = null;
    hasAttachedStreamRef.current = false;
    hls?.destroy();

    if (!video) return;

    video.pause();
    video.removeAttribute('src');
    video.load(); // Ensures the video element is reset to a clean state
  }, []);

  // Attaches a new stream to the video element
  const attachStream = useCallback(
    ({ video, src, requestId, muted, volume, onReady, onFatalError }) => {
      if (!video || !src || !isCurrentRequest(requestId)) return false;

      // Checks if the user's browser supports HLS natively and if it does use the native support instead of HLS.js
      if (!Hls.isSupported()) {
        // Checks if user's browser can play .m3u8 HLS playlists
        if (!video.canPlayType('application/vnd.apple.mpegurl')) return false;

        hasAttachedStreamRef.current = true;
        logHlsDiagnostic('native-hls-start', { src });

        video.crossOrigin = 'use-credentials'; // Includes auth credentials in the request for stream from backend when using native HLS support
        video.src = src;
        video.muted = muted;
        video.volume = volume;

        onReady?.(video, 'Native HLS play');
        return true;
      }

      const hls = new Hls(hlsConfig);
      hlsRef.current = hls;
      hasAttachedStreamRef.current = true;
      hls.loadSource(src); // Provides HLS.js the stream URL to use
      hls.attachMedia(video); // Attaches the HLS.js instance to the HTML video element
      attachHlsDiagnostics({ hls, Hls, video, getAvailableVideoTimeRange }); // Attaches HLS event listeners to the HLS instance
      logHlsDiagnostic('hls-js-start', { src });

      // Fires after successfully parsing the .m3u8 file
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (hlsRef.current !== hls || !isCurrentRequest(requestId)) return;

        video.muted = muted;
        video.volume = volume;
        onReady?.(video, 'HLS play');
      });

      // The attachment survives pause, resume, and soft recovery. Playback request IDs
      // change for those actions, so ongoing HLS events must use attachment identity.
      hls.on(Hls.Events.LEVEL_LOADED, () => {
        if (hlsRef.current === hls) updateReadyVideoProgress();
      });
      hls.on(Hls.Events.BUFFER_APPENDED, () => {
        if (hlsRef.current === hls) updateReadyVideoProgress();
      });

      // HLS handles non-fatal errors itself. Recover its media pipeline when possible;
      // other fatal errors require the caller to restart the current stream.
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (hlsRef.current !== hls || !data?.fatal) return;

        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
          return;
        }

        onFatalError?.(`fatal-${data.type}`);
      });

      return true;
    },
    [isCurrentRequest, updateReadyVideoProgress],
  );

  return useMemo(
    () => ({
      attachStream,
      clearStream,
      hasAttachedStream,
    }),
    [attachStream, clearStream, hasAttachedStream],
  );
};

export default useVideoStreamSource;
